import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { realEstateListingsCollection } from "utils/listings/collections";
import { Stack, Divider, Typography, Box, TextField, CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import type { Listing } from "../../../../../@types/listings/listing";
import type { ChangeEvent, FormEvent } from "react";

type ListingTableAdditionalNotesProps = {
  listing: Listing;
};

export function ListingTableAdditionalNotes({
  listing
}: ListingTableAdditionalNotesProps): JSX.Element {
  const { t: translate } = useTranslation();

  const [additionalNotesForm, setAdditionalNotesForm] = useState("");
  const [loading, setLoading] = useState(false);

  const { additionalNotes } = listing;

  useEffect(() => {
    setAdditionalNotesForm(additionalNotes ?? "");
  }, [additionalNotes]);

  const handleAdditionalNotesSubmit = async (
    e: FormEvent<HTMLFormElement | HTMLInputElement | HTMLTextAreaElement>
  ): Promise<void> => {
    e.preventDefault();

    setLoading(true);

    const trimmedAdditionNotes = additionalNotesForm.trim();

    if (trimmedAdditionNotes === additionalNotes) {
      setLoading(false);
      setAdditionalNotesForm(trimmedAdditionNotes);
      return;
    }

    try {
      await updateDoc(doc(realEstateListingsCollection, listing.id), {
        additionalNotes: trimmedAdditionNotes
      });
    } catch {
      setAdditionalNotesForm(additionalNotes);
    } finally {
      setLoading(false);
    }
  };

  const handleAdditionalNotesChange = ({
    target: { value }
  }: ChangeEvent<HTMLInputElement>): void => {
    setAdditionalNotesForm(value);
  };

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Stack gap={2}>
        <Typography variant="h6">
          {translate("listingsPage.tableColumns.additionalNotes")}
        </Typography>
        <Box component="form" onSubmit={handleAdditionalNotesSubmit}>
          <TextField
            fullWidth
            multiline
            value={additionalNotesForm}
            disabled={loading}
            placeholder={translate("listingsPage.tableColumns.additionalNotes")}
            inputProps={{ sx: { resize: "none", overflow: "hidden" } }}
            InputProps={{
              endAdornment: loading && <CircularProgress size={20} />
            }}
            onChange={handleAdditionalNotesChange}
            onBlur={loading ? undefined : handleAdditionalNotesSubmit}
          />
        </Box>
      </Stack>
    </>
  );
}
